/* ═══ Fila „Etichete” a utilajului de imprimare — linia de ambalare (18 oct 2026; scriere 23 oct; refăcută 24 oct) ═══
 *
 * Dan: „fă o interfață în linia de ambalare, ca un utilaj care să fie imprimarea, cu tabul ei; să apară ce citești de la
 * imprimantă — apoi aici vom da și ce să scrie, sau va lua din MES.” 23 oct: „când linia merge nu stă nimeni să confirme
 * eticheta, merge automat; datele din MES sau manual; data automat”. 24 oct: „să fie foarte ușor de utilizat și foarte
 * intuitivă”.
 *
 * Trei pași, de sus în jos, pe mașina aleasă (M3 / M4 / M7):
 *   1. Ce tipărește acum robotul — eticheta citită din PLC, desenată ca o etichetă;
 *   2. Cine decide eticheta — Automat din MES / Manual / Oprit;
 *   3. (la Manual) Ce să tipărească — câmpurile de pe HMI, previzualizarea etichetei noi și „Trimite la tipărire”.
 * Alegerea „Manual” doar deschide formularul; în PLC pleacă abia la „Trimite la tipărire”. Codul produsului (linia),
 * data și numărul cutiei le pune aplicația (services/etichete_robot.py). Ruta: /api/machines/<id>/etichete (GET) și
 * /api/machines/<id>/etichete/<n> (POST, inginer/master). Culorile: doar variabilele temei (--text, --card-bg,
 * --surface, --border…), ca fila să arate la fel de bine pe tema deschisă și pe cea întunecată.
 */
import { api, escHtml } from './core';

interface Campuri {
    tip: string; dimensiune: string; cod: string; culoare: string; cod_culoare: number; bucati: number;
    bucati_invers?: number; cod_produs: string; data: string; nr_cutie: number; ttr: number; sincronizare?: number;
}
interface Scriere {
    comanda_id: string | null; campuri: Partial<Campuri>; sursa: string; motiv: string | null; nr_cutie: number;
    cine: string | null; pus_la: string; stare: string; confirmare_s: number | null; rezultat_la: string | null;
}
type Mod = 'automat' | 'manual' | 'oprit';
interface Eticheta {
    masina: number; linie: string; stare: string; text: string | null; campuri: Campuri | null;
    citit_la: string | null; varsta_sec: number | null; proaspat: boolean; ordine_octeti_suspecta: boolean;
    mod: Mod; manual: Partial<Campuri>; dorita: Campuri | null; dorita_sursa: string; mes_la: string | null;
    actualizat: { de: string | null; la: string | null } | null; diferente: string[]; jurnal: Scriere[];
}
interface RaspunsEtichete {
    machine_id: string; activ: boolean; plc: string | null; oprita: { la: string; motiv: string } | null;
    blocata_de_colector: boolean; cadru_octeti: number | null; etichete: Eticheta[];
    scriere: { activa: boolean; poate_edita: boolean; data_azi: string; citirea_merge: boolean;
               comanda: { id: string; masina: number } | null };
}

// câmpurile de pe HMI-ul robotului, în ordinea lui; `lung` = octeții din PLC
const CAMPURI: { k: keyof Campuri; et: string; lung?: number; numar?: boolean; ex: string }[] = [
    { k: 'tip', et: 'Tip', lung: 6, ex: 'CAPAC' }, { k: 'dimensiune', et: 'Dimensiune', lung: 10, ex: '38 MM' },
    { k: 'cod', et: 'Cod intern', lung: 6, ex: '1881' }, { k: 'culoare', et: 'Culoare', lung: 12, ex: 'ALB' },
    { k: 'cod_culoare', et: 'Cod culoare', numar: true, ex: '361' }, { k: 'bucati', et: 'Bucăți în cutie', numar: true, ex: '3200' },
];
const MODURI: { m: Mod; titlu: string; text: string }[] = [
    { m: 'automat', titlu: 'Automat din MES', text: 'Eticheta vine din comanda care rulează pe linie, în MES.' },
    { m: 'manual', titlu: 'Manual', text: 'O completezi tu aici și o trimiți la tipărire.' },
    { m: 'oprit', titlu: 'Oprit', text: 'Aplicația nu trimite nimic. Operatorul scrie eticheta pe HMI.' },
];
const CULOARE_MOD: Record<Mod, string> = { automat: 'var(--blue)', manual: 'var(--accent)', oprit: 'var(--text-muted)' };

const _normal = (v: string): string => v.normalize('NFKD').replace(/[̀-ͯ]/g, '').toUpperCase().trim();
const _varsta = (s: number | null): string => s == null ? '' : s < 90 ? `acum ${s} s` : s < 5400 ? `acum ${Math.round(s / 60)} min`
    : s < 172800 ? `acum ${Math.round(s / 3600)} h` : `acum ${Math.round(s / 86400)} zile`;
const _ora = (ts: string | null): string => {
    if (!ts) return '—';
    const d = new Date(ts.replace(' ', 'T') + (/[zZ]|[+-]\d\d:?\d\d$/.test(ts) ? '' : 'Z'));
    return isNaN(d.getTime()) ? ts : d.toLocaleString('ro-RO', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};

function _chip(text: string, culoare: string): string {
    return `<span class="et-chip" style="border:1px solid ${culoare};color:${culoare};border-radius:999px;padding:1px 9px;font-size:.78rem;white-space:nowrap">${escHtml(text)}</span>`;
}

/** Eticheta desenată ca o etichetă de cutie (hârtie albă în ambele teme); `dif` = câmpurile de evidențiat. */
function _desenEticheta(c: Partial<Campuri>, dif: string[] = []): string {
    const s = (k: keyof Campuri, v: string) => dif.includes(k as string)
        ? `<mark style="background:#ffe08a;color:#111;padding:0 2px;border-radius:2px">${escHtml(v)}</mark>` : escHtml(v);
    const rand1 = [c.tip && s('tip', String(c.tip)), c.dimensiune && s('dimensiune', String(c.dimensiune)),
                   c.cod && s('cod', String(c.cod))].filter(Boolean).join(' ');
    const rand2 = [c.culoare && s('culoare', String(c.culoare)), c.cod_culoare ? `cod ${s('cod_culoare', String(c.cod_culoare))}` : '']
        .filter(Boolean).join(' · ');
    const rand3 = [c.bucati != null ? `${s('bucati', Number(c.bucati).toLocaleString('ro-RO'))} buc` : '', c.ttr ? s('ttr', 'TTR') : '']
        .filter(Boolean).join(' · ');
    const rand4 = [c.cod_produs, c.data && (dif.includes('data') ? `<mark style="background:#ffe08a;color:#111;padding:0 2px;border-radius:2px">${escHtml(c.data)}</mark>` : escHtml(c.data)),
                   c.nr_cutie ? `cutia ${c.nr_cutie}` : ''].filter(Boolean).join(' · ');
    return `<div class="et-desen" style="background:#fff;color:#111;border:1px solid #c9ced4;border-radius:6px;padding:10px 12px;
        box-shadow:0 1px 3px rgba(0,0,0,.15);font-family:ui-monospace,Menlo,Consolas,monospace;line-height:1.35;max-width:420px">
        <div style="font-size:1.15rem;font-weight:700;letter-spacing:.02em">${rand1 || '—'}</div>
        <div style="font-size:.95rem">${rand2 || '&nbsp;'}</div>
        <div style="font-size:.95rem;font-weight:600">${rand3 || '&nbsp;'}</div>
        <div style="font-size:.8rem;color:#555;margin-top:2px">${rand4 || '&nbsp;'}</div></div>`;
}

function _stareScriere(s: Scriere): [string, string] {
    if (s.stare === 'confirmat') return s.sursa === 'citit' ? ['era deja în PLC', 'var(--text-muted)']
        : [`✓ tipărită de la următoarea cutie — confirmat în ${String(s.confirmare_s ?? '?').replace('.', ',')} s`, 'var(--green)'];
    const m: Record<string, [string, string]> = {
        trimis: ['se trimite la robot…', 'var(--yellow)'], neconfirmat: ['robotul n-a confirmat', 'var(--red)'],
        eroare: ['eroare la trimitere', 'var(--red)'], refuzat: ['robotul n-a primit conexiunea', 'var(--red)'],
        expirat: ['n-a ajuns la robot (expirat)', 'var(--red)'] };
    return m[s.stare] || [s.stare, 'var(--text-muted)'];
}

const _card = (titlu: string, corp: string, dreapta = '') => `<section class="et-sectiune" style="border:1px solid var(--border);border-radius:10px;padding:12px 14px;background:var(--card-bg)">
    <div style="display:flex;flex-wrap:wrap;align-items:center;justify-content:space-between;gap:6px;margin-bottom:8px">
        <h4 style="margin:0;font-size:1rem;color:var(--text)">${titlu}</h4>${dreapta}</div>${corp}</section>`;

// alegerea «Manual» făcută în filă, încă netrimisă (pe mașină) — formularul se deschide fără să plece nimic în PLC
const _manualDeschis: Record<number, boolean> = {};
let _masinaAleasa: number | null = null;
let _reimprospatare: number | null = null;

function _pagina(e: Eticheta, d: RaspunsEtichete): string {
    const poate = d.scriere.poate_edita;
    const modVazut: Mod = _manualDeschis[e.masina] ? 'manual' : e.mod;

    // 1. ce se tipărește acum
    const citire = e.stare === 'ok'
        ? (e.proaspat ? _chip(`citită ${_varsta(e.varsta_sec)}`, 'var(--green)') : _chip(`veche — ${_varsta(e.varsta_sec)}`, 'var(--yellow)'))
        : e.stare === 'fara_raspuns' ? _chip('robotul n-a răspuns la ultima citire', 'var(--yellow)') : _chip('necitită încă', 'var(--text-muted)');
    const acum = _card(`Ce tipărește acum robotul la ${escHtml(e.linie)}`,
        e.campuri ? _desenEticheta(e.campuri) : `<div style="color:var(--text-muted)">Nicio etichetă citită încă din PLC.</div>`, citire);

    // 2. cine decide
    const optiuni = MODURI.map(o => {
        const ales = modVazut === o.m;
        const nota = o.m === 'automat' && !e.dorita && e.mod !== 'automat' ? ' <span style="color:var(--yellow);font-size:.78rem">(MES nu e legat încă)</span>' : '';
        return `<button type="button" class="et-mod" data-mod="${o.m}" role="radio" aria-checked="${ales}" ${poate ? '' : 'disabled'}
            style="display:flex;gap:10px;align-items:flex-start;text-align:left;width:100%;padding:10px 12px;border-radius:8px;cursor:${poate ? 'pointer' : 'default'};
            border:${ales ? `2px solid ${CULOARE_MOD[o.m]}` : '1px solid var(--border)'};background:${ales ? 'var(--surface)' : 'transparent'};color:var(--text)">
            <span aria-hidden="true" style="flex:none;width:18px;height:18px;margin-top:2px;border-radius:50%;border:2px solid ${ales ? CULOARE_MOD[o.m] : 'var(--border2)'};
                box-shadow:${ales ? 'inset 0 0 0 3px var(--card-bg)' : 'none'};background:${ales ? CULOARE_MOD[o.m] : 'transparent'}"></span>
            <span><strong style="font-size:.95rem">${o.titlu}</strong>${nota}<br><span style="font-size:.83rem;color:var(--text-muted)">${o.text}</span></span>
        </button>`;
    }).join('');
    const cine = _card('Cine decide eticheta', `<div role="radiogroup" aria-label="Cine decide eticheta" style="display:flex;flex-direction:column;gap:6px">${optiuni}</div>
        ${poate ? '' : '<div style="font-size:.82rem;color:var(--text-muted);margin-top:6px">Modul și eticheta le pot schimba inginerii și masterii.</div>'}
        <div class="et-mesaj-mod" role="status" style="font-size:.85rem;margin-top:6px"></div>`);

    // 3. ce să tipărească (Manual) / ce vine din MES (Automat)
    let ce = '';
    if (modVazut === 'manual') {
        const baza: Partial<Campuri> = Object.keys(e.manual || {}).length ? e.manual : (e.campuri || {});
        const campuri = CAMPURI.map(f => `<label style="display:flex;flex-direction:column;gap:3px;font-size:.85rem;color:var(--text-muted);min-width:0">${f.et}
            <input class="et-in" data-k="${f.k}" ${f.numar ? 'type="number" min="0" max="65535" inputmode="numeric"' : `type="text" autocapitalize="characters" maxlength="${(f.lung || 12) + 4}"`}
                value="${escHtml(String(baza[f.k] ?? ''))}" placeholder="${f.ex}" ${poate ? '' : 'disabled'}
                style="font-size:1.05rem;padding:9px 10px;border:1px solid var(--border2);border-radius:7px;background:var(--surface);color:var(--text);min-width:0;width:100%;box-sizing:border-box">
            ${f.lung ? `<span class="et-normal" data-k="${f.k}" style="font-size:.74rem;min-height:1em"></span>` : ''}</label>`).join('');
        ce = _card('Ce să tipărească', `
            <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(140px,1fr));gap:10px">${campuri}</div>
            <label style="display:flex;align-items:center;gap:8px;font-size:.95rem;margin-top:8px;color:var(--text)">
                <input class="et-in" data-k="ttr" type="checkbox" ${(baza.ttr ?? 0) ? 'checked' : ''} ${poate ? '' : 'disabled'} style="width:20px;height:20px"> TTR</label>
            <div style="font-size:.82rem;color:var(--text-muted);margin:8px 0 10px">Se pun singure: cod produs <strong>${escHtml(e.linie)}</strong> ·
                data <strong>${escHtml(d.scriere.data_azi)}</strong> · numărul cutiei (îl numără robotul; de la 1 la produs nou).</div>
            <div style="font-size:.85rem;color:var(--text-muted);margin-bottom:4px">Așa va arăta eticheta:</div>
            <div class="et-previzualizare"></div>
            <div class="et-dif-text" style="font-size:.82rem;color:var(--text-muted);margin-top:4px"></div>
            ${poate ? `<button type="button" class="btn btn-primary et-trimite" style="margin-top:12px;width:100%;max-width:420px;padding:12px;font-size:1rem">Trimite la tipărire</button>
            <div class="et-mesaj" role="status" style="font-size:.9rem;margin-top:8px"></div>` : ''}`);
    } else if (modVazut === 'automat') {
        ce = _card('Eticheta din MES', e.dorita
            ? `${_desenEticheta(e.dorita, e.diferente)}<div style="font-size:.82rem;color:var(--text-muted);margin-top:6px">Se trimite singură la robot când se schimbă produsul sau ziua.${e.mes_la ? ` Din MES, ${_ora(e.mes_la)}.` : ''}</div>`
            : `<div style="color:var(--text-muted)">Legătura cu MES nu e făcută încă (cerută colegilor de platformă). Până atunci nu se trimite nimic — robotul tipărește ce are.</div>`);
    }

    // starea ultimei trimiteri (din ultima oră sau încă în curs) + diferența față de ce se tipărește
    const ultima = e.jurnal.find(s => s.sursa !== 'citit');
    let stare = '';
    if (ultima && (ultima.stare === 'trimis' || Date.now() - new Date(ultima.pus_la).getTime() < 3600e3)) {
        const [t, c] = _stareScriere(ultima);
        stare = `<div class="et-ultima" role="status" style="border-left:3px solid ${c};padding:6px 10px;font-size:.88rem;background:var(--surface);color:var(--text)">
            Ultima trimitere, ${escHtml(_ora(ultima.pus_la))}: <strong style="color:${c}">${escHtml(t)}</strong></div>`;
    }
    const nepotrivit = e.mod !== 'oprit' && e.dorita && e.diferente.length && !d.scriere.comanda && !(ultima && ultima.stare === 'trimis')
        ? `<div style="font-size:.85rem;color:var(--yellow)">Robotul tipărește altceva decât eticheta aleasă (${escHtml(e.diferente.join(', '))}) — probabil schimbată pe HMI. Aplicația n-o suprascrie singură; o trimite din nou la următoarea schimbare.</div>` : '';
    const jurnal = e.jurnal.length ? `<details class="et-jurnal" style="font-size:.84rem;color:var(--text)"><summary style="cursor:pointer;color:var(--text-muted)">Ultimele trimiteri (${e.jurnal.length})</summary>
        <ul style="margin:6px 0 0 18px;padding:0">${e.jurnal.map(s => {
            const [t, c] = _stareScriere(s);
            return `<li style="margin-bottom:3px"><span style="color:var(--text-muted)">${escHtml(_ora(s.pus_la))}</span> · ${escHtml(s.motiv || s.sursa)}${s.cine && s.cine !== 'automat' ? ` (${escHtml(s.cine)})` : ''}:
                <span style="color:${c}">${escHtml(t)}</span><br><span style="color:var(--text-muted)">${escHtml([s.campuri.tip, s.campuri.dimensiune, s.campuri.culoare, s.campuri.bucati ? `${s.campuri.bucati} buc` : '', s.campuri.data].filter(Boolean).join(' · '))}</span></li>`;
        }).join('')}</ul></details>` : '';

    return `<div class="et-card" data-masina="${e.masina}" style="display:flex;flex-direction:column;gap:12px">${acum}${stare}${cine}${ce}${nepotrivit}${jurnal}</div>`;
}

function _valoriFormular(pag: HTMLElement): Record<string, unknown> {
    const v: Record<string, unknown> = {};
    pag.querySelectorAll<HTMLInputElement>('input.et-in').forEach(inp => {
        const k = inp.dataset.k as string;
        v[k] = inp.type === 'checkbox' ? (inp.checked ? 1 : 0) : inp.type === 'number' ? Number(inp.value || 0) : inp.value;
    });
    return v;
}

export async function renderEticheteTab(el: HTMLElement, machineId: string): Promise<void> {
    if (_reimprospatare) { clearTimeout(_reimprospatare); _reimprospatare = null; }
    let d: RaspunsEtichete;
    try {
        d = await api(`/api/machines/${encodeURIComponent(machineId)}/etichete`);
    } catch (err) {
        el.innerHTML = `<div class="alarm-empty">Etichetele nu s-au putut citi de la server: ${escHtml(String(err))}</div>`;
        return;
    }
    // un «Manual» deschis în filă se închide când serverul spune deja manual
    d.etichete.forEach(e => { if (e.mod === 'manual') delete _manualDeschis[e.masina]; });
    if (_masinaAleasa == null || !d.etichete.some(e => e.masina === _masinaAleasa)) _masinaAleasa = d.etichete[0]?.masina ?? null;

    const avert: string[] = [];
    if (d.oprita) avert.push(`Legătura cu PLC-ul robotului pentru etichete e oprită din ${_ora(d.oprita.la)}: ${d.oprita.motiv}`);
    else if (d.blocata_de_colector) avert.push('Colectorul a renunțat singur la etichete (PLC-ul n-a mai răspuns după o cerere) — nu se citește și nu se trimite nimic până la repornirea lui.');
    else if (!d.activ) avert.push('Citirea etichetelor nu e pornită pe colector — se arată ultima etichetă cunoscută.');
    if (d.scriere.comanda) avert.push(`Se trimite acum eticheta pentru M${d.scriere.comanda.masina} — robotul o confirmă în câteva secunde.`);

    const file = d.etichete.map(e => {
        const ales = e.masina === _masinaAleasa;
        const mod: Mod = _manualDeschis[e.masina] ? 'manual' : e.mod;
        return `<button type="button" class="et-fila${ales ? ' active' : ''}" data-masina="${e.masina}" role="tab" aria-selected="${ales}"
            style="flex:1 1 0;min-width:88px;padding:8px 10px;border-radius:8px;cursor:pointer;text-align:center;color:var(--text);
            border:${ales ? '2px solid var(--accent)' : '1px solid var(--border)'};background:${ales ? 'var(--surface)' : 'transparent'}">
            <div style="font-size:1.15rem;font-weight:700">${escHtml(e.linie)}</div>
            <div style="font-size:.76rem;color:${CULOARE_MOD[mod]}">● ${({ automat: 'Automat MES', manual: 'Manual', oprit: 'Oprit' })[mod]}</div></button>`;
    }).join('');

    el.innerHTML = `<div class="et-tab" style="display:flex;flex-direction:column;gap:12px;max-width:760px">
        <div style="display:flex;align-items:center;justify-content:space-between;gap:8px">
            <div style="font-size:.85rem;color:var(--text-muted)">Etichetele cutiilor tipărite de robot. Alege mașina:</div>
            <button type="button" id="et-reimprospateaza" title="Reîmprospătează" aria-label="Reîmprospătează"
                style="flex:none;border:1px solid var(--border);background:transparent;color:var(--text);border-radius:8px;padding:4px 10px;cursor:pointer">↻</button>
        </div>
        <div class="et-file" role="tablist" aria-label="Mașina" style="display:flex;gap:8px">${file}</div>
        ${avert.map(a => `<div class="et-avert" style="border-left:3px solid var(--yellow);padding:6px 10px;background:var(--surface);color:var(--text);font-size:.88rem">${escHtml(a)}</div>`).join('')}
        <div class="et-pagini">${d.etichete.map(e => `<div class="et-pagina" data-masina="${e.masina}" ${e.masina === _masinaAleasa ? '' : 'hidden'}>${_pagina(e, d)}</div>`).join('')}</div>
    </div>`;

    const reda = () => { if (el.isConnected) void renderEticheteTab(el, machineId); };
    el.querySelector('#et-reimprospateaza')?.addEventListener('click', reda);
    el.querySelectorAll<HTMLButtonElement>('.et-fila').forEach(b => b.addEventListener('click', () => {
        _masinaAleasa = Number(b.dataset.masina);
        reda();
    }));

    const posteaza = async (masina: number, corp: Record<string, unknown>) => api(
        `/api/machines/${encodeURIComponent(machineId)}/etichete/${masina}`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(corp) });

    el.querySelectorAll<HTMLElement>('.et-pagina').forEach(pag => {
        const masina = Number(pag.dataset.masina);
        const e = d.etichete.find(x => x.masina === masina)!;
        const mesajMod = pag.querySelector<HTMLElement>('.et-mesaj-mod');
        pag.querySelectorAll<HTMLButtonElement>('.et-mod').forEach(b => b.addEventListener('click', async () => {
            const mod = b.dataset.mod as Mod;
            if (mod === 'manual') {                      // doar deschide formularul — nimic nu pleacă în PLC
                _manualDeschis[masina] = true;
                reda();
                return;
            }
            delete _manualDeschis[masina];
            if (mod === e.mod) { reda(); return; }
            if (mod === 'automat' && !confirm(`${e.linie}: eticheta va veni din MES. Până e legat MES-ul, aplicația nu trimite nimic. Continui?`)) return;
            if (mesajMod) { mesajMod.textContent = 'Se salvează…'; mesajMod.style.color = 'var(--text-muted)'; }
            try { await posteaza(masina, { mod }); reda(); }
            catch (err) { if (mesajMod) { mesajMod.textContent = `Nu s-a salvat: ${String((err as Error).message || err)}`; mesajMod.style.color = 'var(--red)'; } }
        }));

        // previzualizarea etichetei noi, la fiecare tastă: ce pleacă de fapt (fără diacritice, majuscule) + ce diferă
        const prev = pag.querySelector<HTMLElement>('.et-previzualizare');
        const difText = pag.querySelector<HTMLElement>('.et-dif-text');
        const actualizeaza = () => {
            if (!prev) return;
            const v = _valoriFormular(pag);
            const nou: Partial<Campuri> = { cod_produs: e.linie, data: d.scriere.data_azi };
            let prea = false;
            CAMPURI.forEach(f => {
                const brut = v[f.k as string];
                (nou as Record<string, unknown>)[f.k] = f.numar ? Number(brut || 0) : _normal(String(brut || ''));
                const nota = pag.querySelector<HTMLElement>(`.et-normal[data-k="${f.k}"]`);
                if (nota && f.lung) {
                    const n = _normal(String(brut || ''));
                    const lung = n.length > f.lung;
                    prea = prea || lung;
                    nota.textContent = lung ? `prea lung: ${n.length} din ${f.lung} caractere` : (n && n !== String(brut).trim() ? `se trimite: ${n}` : '');
                    nota.style.color = lung ? 'var(--red)' : 'var(--text-muted)';
                }
            });
            nou.ttr = v.ttr ? 1 : 0;
            const c = e.campuri || {} as Partial<Campuri>;
            const dif = (['tip', 'dimensiune', 'cod', 'culoare', 'cod_culoare', 'bucati', 'ttr'] as (keyof Campuri)[])
                .filter(k => String(nou[k] ?? '') !== String(c[k] ?? '')) as string[];
            if (c.data && c.data !== nou.data) dif.push('data');
            prev.innerHTML = _desenEticheta(nou, dif);
            if (difText) difText.textContent = !e.campuri ? '' : dif.length
                ? `Față de ce se tipărește acum se schimbă: ${dif.map(k => (CAMPURI.find(f => f.k === k)?.et || (k === 'ttr' ? 'TTR' : 'data')).toLowerCase()).join(', ')}.`
                : 'E aceeași etichetă care se tipărește acum.';
            const btn = pag.querySelector<HTMLButtonElement>('.et-trimite');
            if (btn) btn.disabled = prea;
        };
        pag.querySelectorAll<HTMLInputElement>('input.et-in').forEach(inp => {
            inp.addEventListener('input', () => { inp.dataset.atins = '1'; actualizeaza(); });
            inp.addEventListener('change', actualizeaza);
        });
        actualizeaza();

        pag.querySelector<HTMLButtonElement>('.et-trimite')?.addEventListener('click', async () => {
            const mesaj = pag.querySelector<HTMLElement>('.et-mesaj');
            const btn = pag.querySelector<HTMLButtonElement>('.et-trimite');
            if (btn) btn.disabled = true;
            if (mesaj) { mesaj.textContent = 'Se trimite la robot…'; mesaj.style.color = 'var(--text-muted)'; }
            try {
                const r = await posteaza(masina, { mod: 'manual', campuri: _valoriFormular(pag) });
                delete _manualDeschis[masina];
                if (mesaj) {
                    mesaj.textContent = /comanda|M\d+:/.test(r.pas || '') ? 'Trimisă. Robotul o confirmă în câteva secunde…'
                        : /deja|nimic de scris/.test(r.pas || '') ? 'Salvată — e deja eticheta care se tipărește.' : `Salvată. ${r.pas || ''}`;
                    mesaj.style.color = 'var(--green)';
                }
                window.setTimeout(reda, 2500);
            } catch (err) {
                if (btn) btn.disabled = false;
                if (mesaj) { mesaj.textContent = `Nu s-a trimis: ${String((err as Error).message || err)}`; mesaj.style.color = 'var(--red)'; }
            }
        });
    });

    // reîmprospătare cât fila e deschisă — des cât se trimite ceva, rar altfel; niciodată peste un formular în lucru
    const inLucru = d.scriere.comanda || d.etichete.some(e => e.jurnal[0]?.stare === 'trimis');
    const planifica = (ms: number) => {
        _reimprospatare = window.setTimeout(() => {
            if (!el.isConnected || !el.querySelector('.et-tab')) return;
            const scrie = el.querySelector('input.et-in[data-atins="1"]') || (document.activeElement as HTMLElement | null)?.closest?.('.et-sectiune');
            if (scrie) { planifica(15000); return; }
            reda();
        }, ms);
    };
    planifica(inLucru ? 3000 : 30000);
}
